import { useMemo, useState } from 'react';
import { X, Search, Store, ChevronLeft } from 'lucide-react';
import { fmtSentDate, type LastSent } from '@/lib/consignmentStoreReport';

export interface ConsignmentStore {
  id: string;
  name: string;
}

interface Props {
  stores: ConsignmentStore[];
  lastSent: Record<string, LastSent>;
  selectedId?: string;
  onPick: (store: ConsignmentStore) => void;
  onClose: () => void;
}

export function ConsignmentStorePickerModal({ stores, lastSent, selectedId, onPick, onClose }: Props) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? stores.filter((s) => s.name.toLowerCase().includes(q)) : stores;
  }, [stores, query]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">בחר חנות לשליחת יתרות</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="סגור">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 pt-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="חיפוש חנות…"
              className="w-full pr-9 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            />
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 && (
            <li className="py-10 text-center text-sm text-gray-400">לא נמצאו חנויות</li>
          )}
          {filtered.map((s) => {
            const sent = lastSent[s.id];
            const selected = s.id === selectedId;
            return (
              <li key={s.id}>
                <button
                  onClick={() => onPick(s)}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-blue-50 transition-colors text-right group ${selected ? 'bg-blue-50 ring-1 ring-blue-200' : ''}`}
                >
                  <span className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                    <Store className="w-4 h-4 text-orange-500" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{s.name}</span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      {selected ? 'החנות הנוכחית · ' : ''}
                      {sent ? `נשלח לאחרונה ${fmtSentDate(sent.last_sent_at)}` : 'טרם נשלח'}
                    </span>
                  </span>
                  <ChevronLeft className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
